import React, {useState, useEffect} from "react";
import axios from "axios";
import Cookies from 'universal-cookie';
import { REF_CURRENT_USER_ID, REF_KEYCLOAK_AUTH_TOKEN } from "nexus-common/src/components/access/constants";
import folder_icon from '../../images/folder_icon.svg'
import file_icon from '../../images/file_icon.svg';
import arrow_back from '../../images/arrow_back.svg';
import arrow_forward from '../../images/arrow_forward.svg';
import grid_view from '../../images/grid_view.svg';
import list_view from '../../images/list_view.svg';
import expand_more from '../../images/expand_more.svg';
import chevron_right from '../../images/chevron_right.svg';
const cookies = new Cookies();

type Directory = {
    name: string;
    type: 'dir';
    size?: number;
    last_modified: string;
    expanded?: boolean;
    contents: (File | Directory)[];
    child: boolean;
}

type File = {
    name: string;
    type: 'file';
    size: number;
    last_modified: string;
}

const FileManager: React.FC = () => {
    const [currentItems, setCurrentItems] = useState<(File | Directory)[]>([]);
    const [currentDir, setCurrentDir] = useState<string>("");
    const [projectIds, setProjectIds] = useState<string[]>([]);
    const [currentProjectId, setCurrentProjectId] = useState<string>('');
    // stack to push and pop directories to naviagate to the previous directory
    const [dirStack, setDirStack] = useState<string[]>([]);
    const [forwardStack, setForwardStack] = useState<string[]>([]);
    const [isGridView, setIsGridView] = useState(true);
    const [expandedDir, setExpandedDir] = useState<Set<string>>(new Set());
    const [activeView, setActiveView] = useState('grid');
    const [loading, setLoading] = useState(true);
    const [fetchingInfo, setFetchingInfo] = useState(false);
    const [directoryPath, setDirectoryPath] = useState<Map<string, {dirpath: string, contentNames: string}>>(new Map());


    useEffect(() => {
        fetchUserProjectsFromAPI(); 
    }, []);

    const fetchUserProjectsFromAPI = async() => {
        try {
            const user_id = cookies.get(REF_CURRENT_USER_ID);
            const token = cookies.get(REF_KEYCLOAK_AUTH_TOKEN);
            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/user_projects`, {user_id, token});
            if(response.data?.projects && response.data.projects.length > 0 ) {
                setProjectIds(response.data.projects);
            } else {
                setProjectIds([]);
            }
        } catch (error) {
            console.error("Error fetching the directories data from api", error);
        } finally {
            setLoading(false);
        }
    }
    
    const fetchDirectoryContents = async(project_id: string, path: string) => {
        setFetchingInfo(true);
        try {
            const user_id = cookies.get(REF_CURRENT_USER_ID);   
            const token = cookies.get(REF_KEYCLOAK_AUTH_TOKEN);
            const response = await axios.post (`${process.env.REACT_APP_SERVER_URL}/api/ls`, {user_id, token, projects: projectIds, project_id, path});
            const items = response.data.map((item: any) => {
                const name = Object.keys(item)[0];
                const [type,size, last_modified] = item[name];
                return { name, type, size, last_modified, expanded: false, contents: []} as File | Directory;
            });
            setCurrentItems(items);
            setCurrentProjectId(project_id);
            return items;
        } catch (error) {
            console.error("Error fetching directory contents");
        } finally {
            setFetchingInfo(false);
        }
    }

    const handleProjectClick = (project_id: string, path: string) => {
        setDirStack([]); // when project is clicked stack is reset
        setForwardStack([]);
        fetchDirectoryContents(project_id, '/')
        .then((output) => {
            setCurrentDir('/');
        });
    }

    const setDirPathOnClick = (directory: Directory) => {
        const dirName = directory.name;
        let dirpath = '';
            let found = false;
            for (const [dirNames, dirInformation] of Array.from(directoryPath.entries())) {
                if(dirInformation.contentNames.includes(dirName)) {
                    found = true;
                    const newPath = dirInformation.dirpath;
                    dirpath = `${newPath}/${dirName}`;
                    break;
                }  
            } 
            if(!found) {
                dirpath = `${currentDir}/${dirName}`;
            }
        return dirpath;
    }

    const handleItemClick = (item: File | Directory) => {
        if (item.type === 'dir') {
            setDirStack([...dirStack, currentDir]); // push the current dir into stack
            setForwardStack([]);
            const newPath = setDirPathOnClick(item);
            fetchDirectoryContents(currentProjectId, newPath)
            .then((output) => {
                 setCurrentDir(newPath);
            });
        }
    }

    const handleBackClick = () => {
        if(dirStack.length > 0) {
            const previousDir = dirStack.pop()!; // ! is to assert value is not undefined
            setForwardStack([...forwardStack, currentDir]);
            setDirStack([...dirStack]);
            fetchDirectoryContents(currentProjectId, previousDir)
            .then((output) => {
                setCurrentDir(previousDir)
            });
        } else {
            setCurrentProjectId('');
            setCurrentItems([]);
            setCurrentDir('');
        }
    }

    const handleForwardClick = () => {
        if(forwardStack.length > 0) {
            const nextDir = forwardStack.shift()!;
            setDirStack([...dirStack, currentDir]);
            setForwardStack([...forwardStack, currentDir]);
            fetchDirectoryContents(currentProjectId, nextDir)
            .then((output) => {
                setCurrentDir(nextDir);
            });
        }
    }


    const handleArrowClick =  async (directory: Directory) => {
        const dirName = directory.name;
        if(expandedDir.has(dirName)) {
            setExpandedDir(new Set(Array.from(expandedDir).filter(name => name !== dirName)));
            const updatedItems = updateDirectoryContents(currentItems, dirName, false);
            setCurrentItems(updatedItems);
        } else {
            const dirpath = setDirPathOnClick(directory);
            const newContents = await fetchDirectoryContents(currentProjectId, dirpath);
            const contentNames = newContents.map((content: { name: string }) => content.name);
            setExpandedDir(new Set([...Array.from(expandedDir), dirName]));
            setDirectoryPath(new Map(directoryPath.set(dirName, {dirpath, contentNames})));
            const updatedItems = updateDirectoryContents(currentItems, dirName, true, newContents);
            setCurrentItems(updatedItems);
        }
    }

    const updateDirectoryContents = (items: (File | Directory)[], dirName: string, expanded: boolean, newContents?: (File|Directory)[]): (File|Directory)[] => {
        return items.map((item) => {
            if(item.type === 'dir' && item.name === dirName) {
                return {
                    ...item, expanded, contents: expanded ? newContents || [] : item.contents
                }
            } else if (item.type === 'dir') {
                return {
                    ...item,
                    contents: updateDirectoryContents((item as Directory).contents, dirName, expanded, newContents)
                }
            }
            return item;
        })
    }

    const formatDate = (dateString: string) => {
        const formattedDate = new Date(dateString)
        .toLocaleString('en-US', {year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true});
        return formattedDate;
    }


    const renderDir = (directory: Directory, path: string, level: number = 0) => {
        const {expanded = false, contents = []} = directory;
        const dirpath = `${path}/${directory.name}`
        return (
        <React.Fragment key={directory.name}>
            <tr onClick={() => handleItemClick(directory)} style={{cursor: 'pointer'}}>
                <td style={{paddingLeft: `${level * 20}px`}}>
                    {directory.contents && (
                        <img src={expanded ? `${expand_more}` : `${chevron_right}`} style={{marginBottom: '-2px'}} onClick={(e) => { e.stopPropagation(); handleArrowClick(directory)}} alt={"collapsible"}/>
                    )}
                    <img src={folder_icon} style={{marginBottom: '-4px'}} alt={"folder_icon"}/>
                    <span>{directory.name}</span>
                </td>
                <td>{formatDate(directory.last_modified)}</td>
                <td>{`--`}</td>
            </tr>
            {expanded && (
                <>
                { contents.length === 0 ? (
                    <div style={{paddingLeft: `${(level +1) * 20}px`, marginBottom: '-2px' }}>
                        This is an empty directory. To add data, please follow the 
                        <a href={process.env.REACT_APP_MYDATA_APD} target="_blank" rel="noreferrer"> guide here.</a>
                    </div>
                ) : (
                    contents.map ( item =>
                        item.type === 'dir' ? renderDir(item as Directory, dirpath, level+1) : (
                            <tr key={item.name} onClick={() => handleItemClick(item)}>
                                <td style={{paddingLeft: `${(level +1) * 20}px` }}>
                                    <img src={file_icon} style={{paddingLeft: `${(level +1) * 20}px`, marginBottom: '-2px' }} alt={"file_icon"}></img>
                                    <span>{item.name}</span>
                                </td>
                                <td>{formatDate(item.last_modified)}</td>
                                <td>{item.size} bytes</td>
                            </tr>
                        )
                    )
                )}
                </>      
            )}
        </React.Fragment>
        )
    }

    const handleListViewClick = () => {
        setActiveView('list');
        setIsGridView(false);
    };

    const handleGridViewClick = () => {
        setActiveView('grid');
        setIsGridView(true);
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', height: '600px', width: '900px'}}> 
            <h1 style={{textAlign: 'center'}}>MyData</h1>
            {loading ? (
                <div className='myDataUIMessage'>
                    <h2>Fetching the active projects, please wait...</h2>
                </div>
            ) : (
                projectIds.length === 0
            ) ? (
            <div className='myDataUIMessage'>
                <h3>No active projects found</h3>
            </div>
            ) : fetchingInfo ? (
                <div className='myDataUIMessage'>
                    <h2>Fetching contents, please wait...</h2>
                </div>
            ) : (
                <>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
                    {(currentProjectId && (dirStack.length > 0 || currentDir === '/')) && (
                        <img src={arrow_back} onClick={handleBackClick} style={{width: '20px', cursor: 'pointer'}} alt={"back_arrow"}/>
                    )}
                    {forwardStack.length > 0 && (
                        <img src={arrow_forward} onClick={handleForwardClick} style={{width: '20px', cursor: 'pointer'}} alt={"forward_arrow"}/>
                    )}
                    <p>Currrent Location: {currentProjectId ? `${currentProjectId}${currentDir}` : 'Projects'}</p>
                    </div>
                    <div style={{display: 'flex'}}>
                        <button className={`grid-list-button ${activeView === 'list' ? 'active' : ''}`} onClick={handleListViewClick}><img src={list_view} alt={"list_view"}/></button>
                        <button className={`grid-list-button ${activeView === 'grid' ? 'active' : ''}`} onClick={handleGridViewClick}><img src={grid_view} alt={"grid_view"}/></button>
                    </div>
                </div>
                <div style={{borderTop: '1px solid black', padding: '10px', overflowY: 'auto', overflowX: 'auto', maxHeight: '450px'}}>
                {currentProjectId === '' ? (
                        <div>
                            <div>
                                {isGridView ? (
                                    <div className="grid-container">
                                        {projectIds.map((projectId) => (
                                            <div key={projectId} className={"grid-item"} onClick={() => handleProjectClick(projectId, `${currentDir}`)} style={{cursor: 'pointer'}}>
                                                <img src={folder_icon} alt={"folder_icon"}/>
                                                <h3>{projectId}</h3>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <table className="list-table">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Project ID</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                    {projectIds.map((projectId) => (
                                        <tr key={projectId} onClick={() => handleProjectClick(projectId, `${currentDir}`)} style={{cursor: 'pointer'}} >
                                            <td><img src={folder_icon} alt={"folder_icon"}/> </td>
                                            <td>{projectId}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                )} 
                            </div> 
                        </div>
                    ) : (
                        <div>
                            {isGridView ? (
                                <div className="grid-container">
                                    {currentItems.length === 0 ? (
                                        <div className='myDataUIMessage'>
                                            <h3 style = {{textAlign: 'center'}}>This is an empty directory. 
                                                <br/>If you are interested to add data to the Active Project Directory(APD), please follow the 
                                                <a href={process.env.REACT_APP_MYDATA_APD} target="_blank" rel="noreferrer"> guide here.</a>
                                            </h3>
                                        </div>
                                    ) : (
                                    (currentItems.map((item: File | Directory) => (                    
                                    <div
                                    key={item.name}
                                    className={"grid-item"}
                                    onClick={() => handleItemClick(item)} style={{cursor: 'pointer'}}
                                    >
                                        {item.type === 'dir' ? <img src={folder_icon} alt={"folder_icon"}/> : <img src={file_icon} alt={"file_icon"}/>}
                                        <h3>{item.name}</h3>
                                    </div>
                                    ))))}
                                </div>
                            ) : (
                                <table className="list-table">
                                    {currentItems.length === 0 ? (
                                        <div className='myDataUIMessage'>
                                            <h3 style = {{textAlign: 'center'}}>This is an empty directory. 
                                            <br/>If you are interested to add data to the Active Project Directory(APD), please follow the 
                                            <a href={process.env.REACT_APP_MYDATA_APD} target="_blank" rel="noreferrer"> guide here.</a>
                                            </h3>
                                        </div>
                                    ) : (
                                    <>
                                    <thead>
                                        <tr>
                                            {/* <th></th> */}
                                            <th style={{paddingLeft: '50px'}}>Name</th>
                                            <th>Last Modified</th>
                                            <th>Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item: File | Directory) => (
                                            item.type === 'dir' ? renderDir(item as Directory, `${item.name}`) : (
                                                <tr key={item.name} style={{ cursor: 'default' }}>
                                                    <td>{<img src={file_icon} alt={"file_icon"}/>} <span>{item.name}</span> </td> 
                                                    <td>{formatDate(item.last_modified)}</td>
                                                    <td>{`${item.size} bytes`}</td>
                                                </tr>
                                            )
                                        ))}
                                    </tbody>
                                    </>
                                    )}
                                </table>
                            )}
                        </div>
                    )}
                    </div>
                </>
            )}
        </div>
    )

}

export default FileManager;